/**
 * @fileoverview กติกาการแนบไฟล์ + บีบอัดรูปภาพฝั่ง client
 * ใช้ร่วมกันระหว่างหน้า Work Request (admin) และหน้าแจ้งงานของลูกค้า (customer)
 * เพื่อให้เงื่อนไขจำนวนไฟล์/ขนาดไฟล์/ข้อความแจ้งเตือน ตรงกันทุกหน้า
 * @module utils/fileUpload
 */

/**
 * กติกาการแนบไฟล์ของ Work Request
 * - maxFiles: จำนวนไฟล์สูงสุดต่อ 1 ใบแจ้งงาน (นับรวมไฟล์เดิมที่แนบไว้แล้ว)
 * - maxSize : ขนาดต่อไฟล์ "ก่อน" บีบอัด (byte)
 *
 * รูปภาพ: 10 MB ครอบคลุมรูปจากมือถือ/กล้องเกือบทั้งหมด (12–48MP JPEG ≈ 3–8 MB)
 *         และถูกบีบอัดต่อให้เหลือราว 200–800 KB ก่อนอัปโหลดจริง
 * วิดีโอ: 15 MB ≈ คลิป 1080p ความยาว 10–20 วินาที ซึ่งเพียงพอต่อการอธิบายอาการเสีย
 *         (เบราว์เซอร์บีบอัดวิดีโอเองไม่ได้ จึงคุมด้วยขนาดไฟล์แทน)
 */
export const WORK_REQUEST_UPLOAD_RULES = {
  image: { maxFiles: 10, maxSize: 10 * 1024 * 1024 },
  video: { maxFiles: 3, maxSize: 15 * 1024 * 1024 },
};

/** ด้านที่ยาวที่สุดของรูปหลังบีบอัด (px) — พอสำหรับดูรายละเอียดอาการเสียบนจอ Full HD */
const IMAGE_MAX_DIMENSION = 1920;

/** คุณภาพของรูปหลังบีบอัด (0-1) */
const IMAGE_QUALITY = 0.8;

/** ไฟล์ที่เล็กกว่านี้ไม่ต้องบีบ เพราะบีบแล้วแทบไม่ลดขนาด */
const IMAGE_SKIP_COMPRESS_BELOW = 200 * 1024;

/** ชนิดรูปที่ห้ามบีบ — gif จะเสีย animation, svg เป็น vector ที่เล็กอยู่แล้ว */
const NO_COMPRESS_MIME = ["image/gif", "image/svg+xml"];

/** นามสกุลสำรองไว้เดาชนิดไฟล์ กรณีมือถือบางรุ่นส่ง file.type มาเป็นค่าว่าง */
const EXTENSIONS_BY_TYPE = {
  image: ["jpg", "jpeg", "png", "gif", "webp", "bmp", "heic", "heif"],
  video: ["mp4", "mov", "m4v", "webm", "avi", "mkv"],
};

/**
 * แทนค่า {key} ในข้อความแปลภาษา (t() ของโปรเจกต์ไม่รองรับ interpolation)
 * @param {string} template - เช่น "สูงสุด {count} ภาพ"
 * @param {Object<string, string|number>} vars
 * @returns {string}
 */
export const formatText = (template, vars = {}) =>
  String(template || "").replace(/\{(\w+)\}/g, (match, key) => (vars[key] !== undefined ? String(vars[key]) : match));

/**
 * แปลง byte เป็นข้อความอ่านง่าย
 * @param {number} bytes
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes < 0) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/** byte -> MB (จำนวนเต็ม) สำหรับใช้ในข้อความแจ้งเตือน */
export const toMB = (bytes) => Math.round(bytes / 1024 / 1024);

/**
 * เช็กว่าไฟล์เป็นชนิดที่ต้องการหรือไม่ (ดูจาก mime ก่อน ถ้าว่างค่อยดูนามสกุล)
 * @param {File} file
 * @param {"image"|"video"} fileType
 * @returns {boolean}
 */
const isFileOfType = (file, fileType) => {
  if (file?.type) return file.type.startsWith(`${fileType}/`);
  const ext = (file?.name || "").split(".").pop()?.toLowerCase();
  return (EXTENSIONS_BY_TYPE[fileType] || []).includes(ext);
};

/**
 * คัดไฟล์ตามกติกา: ชนิดไฟล์ -> ขนาดต่อไฟล์ -> จำนวนไฟล์คงเหลือ
 * ไม่ยิง alert เอง เพื่อให้หน้าเรียกใช้จัดข้อความ (t()) ได้เอง
 *
 * @param {FileList|File[]} fileList - ไฟล์ที่ผู้ใช้เลือก/ลากมาวาง
 * @param {"image"|"video"} fileType
 * @param {number} currentCount - จำนวนไฟล์ที่แนบอยู่แล้วในฟอร์ม (นับไฟล์เดิมตอนแก้ไขด้วย)
 * @param {Object} [rules] - กติกาที่ใช้ (ค่าเริ่มต้น WORK_REQUEST_UPLOAD_RULES)
 * @returns {{ accepted: File[], wrongType: string[], tooBig: string[], overLimit: number, rule: {maxFiles: number, maxSize: number} }}
 */
export const pickFilesByRule = (fileList, fileType, currentCount = 0, rules = WORK_REQUEST_UPLOAD_RULES) => {
  const rule = rules[fileType] || rules.image;
  const arr = Array.from(fileList || []);

  const wrongType = [];
  const tooBig = [];

  const matched = arr.filter((file) => {
    if (isFileOfType(file, fileType)) return true;
    wrongType.push(file.name);
    return false;
  });

  const sized = matched.filter((file) => {
    if (file.size <= rule.maxSize) return true;
    tooBig.push(file.name);
    return false;
  });

  const room = Math.max(0, rule.maxFiles - currentCount);
  const accepted = sized.slice(0, room);

  return { accepted, wrongType, tooBig, overLimit: sized.length - accepted.length, rule };
};

/** จำค่าว่าเบราว์เซอร์ export webp ได้ไหม (เช็กครั้งเดียวพอ) */
let webpSupported = null;
const supportsWebp = () => {
  if (webpSupported === null) {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 1;
      canvas.height = 1;
      webpSupported = canvas.toDataURL("image/webp").startsWith("data:image/webp");
    } catch (e) {
      webpSupported = false;
    }
  }
  return webpSupported;
};

/**
 * เลือกชนิดไฟล์ปลายทางหลังบีบอัด
 * webp ให้ขนาดเล็กที่สุดและยังคงพื้นหลังโปร่งใส
 * ถ้าเบราว์เซอร์ไม่รองรับ ให้ png คงเป็น png (ไม่งั้นพื้นหลังโปร่งใสจะกลายเป็นสีดำ)
 */
const pickOutputType = (file) => {
  if (supportsWebp()) return "image/webp";
  return file.type === "image/png" ? "image/png" : "image/jpeg";
};

/** เปลี่ยนนามสกุลไฟล์ให้ตรงกับชนิดไฟล์หลังบีบอัด */
const renameByMime = (fileName, mimeType) => {
  const ext = (mimeType || "").split("/")[1] || "jpg";
  const base = (fileName || "image").replace(/\.[^.]+$/, "");
  return `${base}.${ext}`;
};

/** โหลดไฟล์รูปเข้า <img> พร้อมจัดการ error/หมดเวลา (เช่น HEIC ที่เบราว์เซอร์ decode ไม่ได้) */
const loadImageElement = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const timer = setTimeout(() => {
      URL.revokeObjectURL(url);
      reject(new Error("decode timeout"));
    }, 15000);

    img.onload = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      reject(new Error("decode failed"));
    };
    img.src = url;
  });

/**
 * บีบอัดรูป 1 ไฟล์ด้วย canvas (ย่อด้านยาวสุดเหลือ IMAGE_MAX_DIMENSION + ลดคุณภาพ)
 * ปลอดภัยเสมอ: ถ้าบีบไม่ได้ หรือบีบแล้วไม่เล็กลง จะคืนไฟล์ต้นฉบับกลับไป
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
export const compressImageFile = async (file) => {
  if (!file) return file;
  // เรียกกับไฟล์ชนิดใดก็ได้อย่างปลอดภัย (เช่นโซนที่แนบ pdf/doc/xls ปนกับรูป)
  if (!file.type || !file.type.startsWith("image/")) return file;
  if (NO_COMPRESS_MIME.includes(file.type)) return file;
  if (file.size <= IMAGE_SKIP_COMPRESS_BELOW) return file;

  try {
    const img = await loadImageElement(file);
    const ratio = Math.min(1, IMAGE_MAX_DIMENSION / Math.max(img.width, img.height));

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * ratio));
    canvas.height = Math.max(1, Math.round(img.height * ratio));

    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const outputType = pickOutputType(file);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, outputType, IMAGE_QUALITY));

    // บีบแล้วใหญ่กว่าเดิม (เช่นรูปที่ถูกบีบมาดีอยู่แล้ว) -> ใช้ไฟล์เดิมดีกว่า
    if (!blob || blob.size >= file.size) return file;

    // toBlob อาจ fallback เป็น png เมื่อ type ที่ขอไม่รองรับ จึงยึด blob.type เป็นหลัก
    const finalType = blob.type || outputType;
    return new File([blob], renameByMime(file.name, finalType), { type: finalType, lastModified: Date.now() });
  } catch (e) {
    // decode ไม่ได้ (HEIC, ไฟล์เสีย) -> ปล่อยไฟล์ต้นฉบับไปตามเดิม
    return file;
  }
};

/**
 * บีบอัดรูปหลายไฟล์ (ทีละไฟล์ เพื่อไม่ให้ decode พร้อมกันจนหน่วยความจำพุ่ง)
 * @param {File[]} files
 * @returns {Promise<File[]>}
 */
export const compressImageFiles = async (files) => {
  const out = [];
  for (const file of files || []) {
    out.push(await compressImageFile(file));
  }
  return out;
};

const fileUploadUtils = {
  WORK_REQUEST_UPLOAD_RULES,
  formatText,
  formatFileSize,
  toMB,
  pickFilesByRule,
  compressImageFile,
  compressImageFiles,
};

export default fileUploadUtils;
